import { TrendingUp, TrendingDown, Minus } from 'lucide-react'

interface RankingKeywordRow {
  id: number
  position: number | null
  delta: number | null
}

interface Distribution {
  top1: number
  top3: number
  top5: number
  top10: number
  top30: number
  notRanked: number
}

interface StatsCardsProps {
  keywords: RankingKeywordRow[]
  distribution: Distribution
  visibility: number
}

const posRanges = [
  { label: 'Top 1', value: (d: Distribution) => d.top1, bg: 'bg-emerald-500/15', text: 'text-emerald-600' },
  { label: 'Top 3', value: (d: Distribution) => d.top3, bg: 'bg-green-500/15', text: 'text-green-600' },
  { label: 'Top 10', value: (d: Distribution) => d.top10, bg: 'bg-blue-500/15', text: 'text-blue-600' },
  { label: 'Top 30', value: (d: Distribution) => d.top30, bg: 'bg-yellow-500/15', text: 'text-yellow-600' },
  { label: 'Non classe', value: (d: Distribution) => d.notRanked, bg: 'bg-gray-100', text: 'text-gray-500' },
]

const visSegments = [
  { key: 'top1', color: '#10B981', label: 'Top 1' },
  { key: 'top3', color: '#84CC16', label: 'Top 3' },
  { key: 'top10', color: '#6366F1', label: 'Top 10' },
  { key: 'top30', color: '#FBBF24', label: 'Top 30' },
  { key: 'notRanked', color: '#D1D5DB', label: 'N/C' },
] as const

function getVisibilityColor(v: number): string {
  if (v >= 70) return '#10B981'
  if (v >= 40) return '#84CC16'
  if (v >= 20) return '#FBBF24'
  if (v >= 10) return '#F97316'
  return '#EF4444'
}

export default function StatsCards({ keywords, distribution, visibility }: StatsCardsProps) {
  const improved = keywords.filter(k => k.delta !== null && k.delta > 0).length
  const declined = keywords.filter(k => k.delta !== null && k.delta < 0).length
  const stable = keywords.filter(k => k.delta === 0).length
  const totalChanged = improved + declined
  const totalTracked = keywords.length
  const visColor = getVisibilityColor(visibility)
  const distTotal = distribution.top1 + distribution.top3 + distribution.top10 + distribution.top30 + distribution.notRanked

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {/* Card 1 - Positions dans le classement */}
      <div className="bg-white border border-[#E2E4ED] rounded-xl p-5">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-[#555A73] mb-3">
          Positions dans le classement
        </p>
        <div className="flex items-baseline gap-2 mb-3">
          <span className="text-3xl font-bold text-[#1B2559] tabular-nums">
            {totalTracked.toLocaleString('fr-FR')}
          </span>
          <span className="text-xs text-[#8B8FA7]">mots-cles suivis</span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {posRanges.map(r => {
            const count = r.value(distribution)
            return (
              <span
                key={r.label}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold ${r.bg} ${r.text}`}
              >
                {count}
                <span className="font-normal text-[10px] opacity-70">{r.label}</span>
              </span>
            )
          })}
        </div>
      </div>

      {/* Card 2 - Mots-cles avec changements */}
      <div className="bg-white border border-[#E2E4ED] rounded-xl p-5">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-[#555A73] mb-3">
          Mots-cles avec changements
        </p>
        <div className="flex items-baseline gap-2 mb-4">
          <span className="text-3xl font-bold text-[#1B2559] tabular-nums">
            {totalChanged.toLocaleString('fr-FR')}
          </span>
          <span className="text-xs text-[#8B8FA7]">ont change de position</span>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <div className="flex items-center justify-center w-6 h-6 rounded-lg bg-emerald-500/15">
              <TrendingUp className="w-3.5 h-3.5 text-emerald-600" strokeWidth={1.5} />
            </div>
            <span className="text-sm font-bold text-emerald-600 tabular-nums">{improved}</span>
            <span className="text-[10px] text-[#8B8FA7]">hausse</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="flex items-center justify-center w-6 h-6 rounded-lg bg-red-500/15">
              <TrendingDown className="w-3.5 h-3.5 text-red-500" strokeWidth={1.5} />
            </div>
            <span className="text-sm font-bold text-red-500 tabular-nums">{declined}</span>
            <span className="text-[10px] text-[#8B8FA7]">baisse</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="flex items-center justify-center w-6 h-6 rounded-lg bg-gray-100">
              <Minus className="w-3.5 h-3.5 text-gray-400" strokeWidth={1.5} />
            </div>
            <span className="text-sm font-bold text-gray-500 tabular-nums">{stable}</span>
            <span className="text-[10px] text-[#8B8FA7]">stable</span>
          </div>
        </div>
      </div>

      {/* Card 3 - Score de visibilite */}
      <div className="bg-white border border-[#E2E4ED] rounded-xl p-5">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-[#555A73] mb-3">
          Score de visibilite
        </p>
        <div className="flex items-baseline gap-1 mb-4">
          <span className="text-3xl font-bold tabular-nums" style={{ color: visColor }}>
            {visibility}%
          </span>
          <span className="text-xs text-[#8B8FA7]">pondere par position</span>
        </div>
        {distTotal > 0 && (
          <div className="flex h-2.5 rounded-full overflow-hidden bg-[#F4F5FA]">
            {visSegments.map(seg => {
              const val = distribution[seg.key]
              if (val === 0) return null
              return (
                <div
                  key={seg.key}
                  className="h-full transition-all duration-500"
                  style={{
                    width: `${(val / distTotal) * 100}%`,
                    backgroundColor: seg.color,
                  }}
                />
              )
            })}
          </div>
        )}
        {distTotal > 0 && (
          <div className="flex gap-3 mt-2.5">
            {visSegments.map(seg => {
              const val = distribution[seg.key]
              if (val === 0) return null
              return (
                <div key={seg.key} className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: seg.color }} />
                  <span className="text-[10px] text-[#8B8FA7] tabular-nums">{val}</span>
                  <span className="text-[9px] text-[#B0B4C5]">{seg.label}</span>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
